import { GraphQLClient, type RequestMiddleware } from 'graphql-request'
import { openDB } from 'idb'
import ky, { type BeforeErrorHook, type BeforeRequestHook, type KyRequest } from 'ky'
import { z } from 'zod'
import { authManager } from '../auth-manager.ts'
import { encodeImpersonation, type Impersonation } from '../data/impersonation.ts'
import { useChartParameters } from '../states.ts'

export const applyAuthHeaderHook: BeforeRequestHook = async (request: KyRequest) => {
	if (authManager.isAuthenticated()) {
		const token = await authManager.getAccessToken()
		request.headers.set('Authorization', `Bearer ${token}`)
	}
}

export const fetch = ky.create({
	hooks: {
		beforeRequest: [applyAuthHeaderHook],
		afterResponse: [
			async (_input, _options, response) => {
				if (response.status === 401) await logout()
			},
		],
	},
})

export const slowFetch = fetch.extend({ timeout: 29_000 })

export const createApiClient = ({
	prefixUrl,
	headers,
	timeout,
	hooks,
}: {
	prefixUrl: string
	headers?: Record<string, string>
	timeout?: number | false
	hooks?: { beforeError?: BeforeErrorHook[] }
}) =>
	ky.create({
		prefixUrl: prefixUrl,
		headers: {
			Accept: 'application/json',
			...headers,
		},
		retry: 0,
		timeout,
		hooks: {
			beforeRequest: [applyAuthHeaderHook],
			beforeError: hooks?.beforeError,
		},
	})

export const graphqlRequestMiddleware: RequestMiddleware = async (request) => {
	if (!authManager.isAuthenticated()) {
		return request
	}
	const accessToken = await authManager.getAccessToken()
	return {
		...request,
		headers: {
			...request.headers,
			Authorization: `Bearer ${accessToken}`,
		},
	}
}

export const createGraphQLClient = (endpoint: string, impersonation?: Impersonation) => {
	const url = impersonation ? `${endpoint}?impersonation=${encodeImpersonation(impersonation)}` : endpoint
	return new GraphQLClient(url, {
		requestMiddleware: graphqlRequestMiddleware,
	})
}

export const handleResponse = <T, E>({ data, error }: { data?: T; error?: E }) => {
	if (error) throw error
	return data as T
}

/** @param userId - Clear user-specific cached data if provided */
export const logout = async (userId?: string) => {
	if (userId) useChartParameters.getState().forgetUser(userId)
	await queryCacheCleanup()
	await authManager.logout()
}

const queryCacheCleanup = async () => {
	try {
		const db = await openDB('reactQuery', 1)
		await db.delete('queryCache', 'queryCache')
	} catch (error) {
		console.error('Failed to clear query cache:', error)
	}
}

// GraphQL serializes missing fields as `null`. In zod (and TS in general) `undefined` is nicer;
// with `.optional()` we can use `.default()` for example, with `.nullish()` we can't. So let's
// preprocess data to replace `null` with `undefined`.
// Once/if we move away from GraphQL API to a normal REST API, we should be able to get rid of this.
const nullsToUndefined = (value: unknown): unknown => {
	if (value === null) return undefined
	if (Array.isArray(value)) return value.map(nullsToUndefined)
	if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, nullsToUndefined(v)]))
	return value
}

export const stripNulls = <T extends z.ZodType>(schema: T) => z.preprocess(nullsToUndefined, schema)
